import { useCallback, useEffect, useRef } from 'react';

import type useNativeAction from '@/components/remote-software/hooks/useNativeAction';
import type { NativeTokenPayload } from '@/config/nativeTokenStore';

import msalInstance from '@/config/MsalAuthConfig';
import { registerNativeBridgeRefresh, setNativeTokens, unregisterNativeBridgeRefresh } from '@/config/nativeTokenStore';
import { queryClient } from '@/config/QueryConfig';

/**
 * Bridges native auth tokens into the web app's token system.
 *
 * - Listens for VPC_provide_auth_tokens events from native
 * - Stores tokens in nativeTokenStore + hydrates MSAL account
 * - Sends VPC_session_active confirmation back to native
 * - Registers a 2-step refresh callback for getToken() to invoke:
 *   1. sendRequestToNative('refreshToken') — silent, 15s timeout
 *   2. sendEventToNative('sessionExpired') — wait for provideAuthTokens event, 30s
 */
const useNativeAuthBridge = (isNative: boolean, nativeAction: ReturnType<typeof useNativeAction>) => {
  // Deduplicate concurrent refresh requests
  const refreshInProgress = useRef<Promise<NativeTokenPayload> | null>(null);

  // Pending resolve/reject for VPC_provide_auth_tokens wait (fallback path)
  const pendingTokenEvent = useRef<{
    resolve: (tokens: NativeTokenPayload) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  } | null>(null);

  /**
   * Hydrate MSAL with the account info from native tokens.
   */
  const hydrateMsalAccount = useCallback((account: NativeTokenPayload['account'], idToken?: string) => {
    try {
      // Construct an MSAL-compatible AccountInfo and set it as active
      msalInstance.setActiveAccount({
        homeAccountId: account.homeAccountId,
        localAccountId: account.localAccountId,
        environment: account.environment,
        tenantId: account.tenantId,
        username: account.username,
        idToken,
      });
      console.info('[NativeAuthBridge] MSAL account hydrated', {
        homeAccountId: account.homeAccountId,
      });
    } catch (error) {
      console.error('[NativeAuthBridge] Failed to hydrate MSAL account', error);
    }
  }, []);

  /**
   * Process incoming native auth tokens.
   * Called on VPC_provide_auth_tokens events and on refresh responses.
   */
  const processNativeTokens = useCallback(
    (payload: unknown) => {
      const tokens = payload as NativeTokenPayload;

      if (!tokens?.accessToken) {
        console.error('[NativeAuthBridge] Received invalid token payload', payload);
        return;
      }

      // Store in module-level store (accessible by getToken())
      setNativeTokens(tokens);

      // Hydrate MSAL account
      if (tokens.account) {
        hydrateMsalAccount(tokens.account, tokens.idToken);
      }

      // Invalidate all queries so failed/stale queries refetch with fresh tokens
      queryClient.invalidateQueries();

      // Send VPC_session_active confirmation to native
      nativeAction.sendEventToNative('sessionActive', {
        status: 'authenticated',
        timestamp: Date.now(),
      });

      // If there's a pending token event wait (fallback path), resolve it
      if (pendingTokenEvent.current) {
        clearTimeout(pendingTokenEvent.current.timeoutId);
        pendingTokenEvent.current.resolve(tokens);
        pendingTokenEvent.current = null;
      }

      console.info('[NativeAuthBridge] Native tokens processed successfully', {
        expiresOn: new Date(tokens.expiresOn).toISOString(),
        hasAccount: !!tokens.account,
      });
    },
    [hydrateMsalAccount, nativeAction]
  );

  /**
   * Request token refresh from native — 2-step strategy:
   *  1. Send refreshToken request (expects nativeToWebResponse, 15s timeout)
   *  2. On failure: send sessionExpired event, wait for provideAuthTokens event (30s)
   */
  const requestTokenRefresh = useCallback(async (): Promise<NativeTokenPayload> => {
    // Deduplicate concurrent refresh requests
    if (refreshInProgress.current) {
      return refreshInProgress.current;
    }

    refreshInProgress.current = (async () => {
      // Strategy 1: Silent request/response
      try {
        console.info('[NativeAuthBridge] Attempting silent refresh via refreshToken request');
        const response = (await nativeAction.sendRequestToNative('refreshToken', undefined, {
          requestTimeout: 15000,
        })) as NativeTokenPayload;

        // The response payload may be nested under .payload or at top level
        const tokens = (response as unknown as { payload?: NativeTokenPayload })?.payload ?? response;

        if (tokens?.accessToken) {
          setNativeTokens(tokens);
          if (tokens.account) {
            hydrateMsalAccount(tokens.account, tokens.idToken);
          }
          nativeAction.sendEventToNative('sessionActive', {
            status: 'refreshed',
            timestamp: Date.now(),
          });
          return tokens;
        }

        throw new Error('refreshToken response did not contain valid tokens');
      } catch (requestError) {
        console.warn('[NativeAuthBridge] refreshToken request failed, falling back to sessionExpired', requestError);

        // Strategy 2: Fire sessionExpired event, wait for provideAuthTokens event
        return new Promise<NativeTokenPayload>((resolve, reject) => {
          const FALLBACK_TIMEOUT = 30_000;

          const timeoutId = setTimeout(() => {
            if (pendingTokenEvent.current) {
              pendingTokenEvent.current = null;
              reject(
                new Error('[NativeAuthBridge] Timed out waiting for VPC_provide_auth_tokens after sessionExpired')
              );
            }
          }, FALLBACK_TIMEOUT);

          // Store pending promise handlers — processNativeTokens will resolve this
          pendingTokenEvent.current = { resolve, reject, timeoutId };

          // Fire the session expired event
          nativeAction.sendEventToNative('sessionExpired', {
            reason: 'token_expired',
            timestamp: Date.now(),
          });
        });
      }
    })().finally(() => {
      refreshInProgress.current = null;
    });

    return refreshInProgress.current;
  }, [nativeAction, hydrateMsalAccount]);

  // Register event listener for VPC_provide_auth_tokens
  useEffect(() => {
    const unregister = nativeAction.registerEventListener('provideAuthTokens', processNativeTokens);
    return unregister;
  }, [nativeAction, processNativeTokens]);

  // Register the bridge refresh callback so getToken() can invoke it
  useEffect(() => {
    registerNativeBridgeRefresh(requestTokenRefresh);
    return () => {
      unregisterNativeBridgeRefresh();
    };
  }, [requestTokenRefresh]);

  // Proactively request tokens from native after handshake completes
  useEffect(() => {
    if (!isNative) return;

    requestTokenRefresh().catch((err) => {
      console.error('[NativeAuthBridge] Initial token request failed', err);
    });
  }, [isNative, requestTokenRefresh]);

  // Cleanup pending token event on unmount
  useEffect(() => {
    return () => {
      if (pendingTokenEvent.current) {
        clearTimeout(pendingTokenEvent.current.timeoutId);
        pendingTokenEvent.current.reject(new Error('[NativeAuthBridge] Hook unmounted while waiting for tokens'));
        pendingTokenEvent.current = null;
      }
    };
  }, []);
};

export default useNativeAuthBridge;
